import { useEffect, useMemo } from 'react';
import { CostPriceAnalysis } from '../services/costPrice/costPriceService';
import { collectSourceRestaurantIds } from '../utils/costPriceUtils';
import { useSourceRestaurantDataStore } from '../stores/appSourceRestaurantDataStore';
import { OrderHistory } from '../stores/appOrderHistoryStore';
import { CustomIngredient } from '../services/customIngredients/customIngredientsService';
import { Subrecipe } from '../services/subrecipes/subrecipesService';

export interface ByRestaurantCostData {
  orders: Map<string, OrderHistory[]>;
  custom: Map<string, CustomIngredient[]>;
  subrecipes: Map<string, Map<string, Subrecipe>>;
}

/**
 * Cross-restaurant sourcing helper shared by every page that enriches cost-price
 * analyses (cost price page, detail modal, charts, menus). Collects the distinct
 * `sourceRestaurantId`s referenced by `analyses`, lazily loads exactly those
 * siblings' data sets, and returns the keyed maps to pass to
 * `enrichCostPrice*WithCurrentPrices` via `opts.byRestaurant` (+ `currentRestaurantId`).
 *
 * Without this, a borrowed ingredient resolves against the current restaurant's
 * orders only → no match → €0 cost. Returns empty maps when nothing is
 * cross-restaurant (zero extra fetches).
 */
export function useByRestaurantCostData(
  analyses: CostPriceAnalysis[],
  currentRestaurantId?: string,
  enabled = true
): ByRestaurantCostData {
  const sourceDataByRestaurant = useSourceRestaurantDataStore(s => s.dataByRestaurant);
  const ensureSourceLoaded = useSourceRestaurantDataStore(s => s.ensureLoaded);

  const referencedSiblingIds = useMemo(
    () => enabled ? collectSourceRestaurantIds(analyses, currentRestaurantId) : [],
    [analyses, currentRestaurantId, enabled]
  );

  useEffect(() => {
    referencedSiblingIds.forEach(id => ensureSourceLoaded(id));
  }, [referencedSiblingIds, ensureSourceLoaded]);

  return useMemo(() => {
    const orders = new Map<string, OrderHistory[]>();
    const custom = new Map<string, CustomIngredient[]>();
    const subrecipes = new Map<string, Map<string, Subrecipe>>();
    referencedSiblingIds.forEach(id => {
      const data = sourceDataByRestaurant[id];
      if (!data) return;
      orders.set(id, data.orders);
      custom.set(id, data.customIngredients);
      subrecipes.set(id, data.subrecipes);
    });
    return { orders, custom, subrecipes };
  }, [referencedSiblingIds, sourceDataByRestaurant]);
}
